import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  getBillingCycleEoePreview,
  getBillingCycleHippsPreview,
  getAuthorizationErrors,
  getPatientVisits,
  listEpisodeAuthorizations,
} from '../../services/api';
import type {
  AuthorizationError,
  AuthorizationResponse,
  EoePreviewResponse,
  GetBillingCyclesResponse,
  VisitResponse,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { Link } from 'react-router-dom';
import {
  billingOrderPath,
  patientAuthorizationsPath,
  patientEpisodePath,
  patientVisitPath,
} from '../../utils/navigation';
import { scrollToField } from '../../utils/scrollToField';
import {
  buildInsuranceIdFields,
  usesPatientInsuranceId,
} from './worksheet/insuranceIdFields';
import {
  STATUS_DISPLAY_LABELS,
  getStatusBadgeVariant,
} from '../task/TaskDetailPanel';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import { useNavigateWithNewTab } from '../../hooks/useNavigateWithNewTab';

export interface EOEPreviewPopupProps {
  billingCycle: GetBillingCyclesResponse | null;
  onOpenChange: (open: boolean) => void;
  /** Draft cycles on the Ready tab use `true`; Not Ready uses `false`. */
  isDraftReady?: boolean;
}

// Hidden for now per ROG-2716 (Pedram) — flip to re-enable the F2F check row.
const SHOW_F2F_CHECK = false;

type CheckStatus = 'pass' | 'fail';

interface CheckItem {
  key: string;
  label: string;
  status: CheckStatus;
  message: string;
  anchor?: string;
}

interface FieldDef {
  label: string;
  value: string;
  mono?: boolean;
  span2?: boolean;
  anchor?: string;
}

// The six discipline budget fields tracked by authorization checks (matches AuthorizationsTab).
const AUTH_DISCIPLINE_FIELDS: {
  key: keyof AuthorizationResponse;
  label: string;
}[] = [
  { key: 'sn_visits', label: 'SN' },
  { key: 'pt_visits', label: 'PT' },
  { key: 'ot_visits', label: 'OT' },
  { key: 'st_visits', label: 'ST' },
  { key: 'msw_visits', label: 'MSW' },
  { key: 'hha_visits', label: 'HHA' },
];

const EOEPreviewPopup: React.FC<EOEPreviewPopupProps> = ({
  billingCycle,
  onOpenChange,
}) => {
  const [preview, setPreview] = useState<EoePreviewResponse | null>(null);
  const [hippsCode, setHippsCode] = useState<string | null>(null);
  // Server-supplied display copy naming the OASIS item or diagnosis to fix.
  // Set only for refusals a biller can act on; null otherwise.
  const [hippsUnavailableReason, setHippsUnavailableReason] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authData, setAuthData] = useState<{
    auths: AuthorizationResponse[];
    errors: AuthorizationError[];
    visits: VisitResponse[];
  } | null>(null);
  const [authLoadFailed, setAuthLoadFailed] = useState(false);

  const navigateTo = useNavigateWithNewTab();
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const canViewOrders = hasPermission(PermissionSlug.ORDER_MANAGER);

  // Plain click navigates + closes the dialog; Cmd/Ctrl+click opens a new tab
  // and leaves the dialog open (handled inside useNavigateWithNewTab).
  const navigateAndClose = (path: string) => (e: React.MouseEvent) => {
    if (!e.metaKey && !e.ctrlKey) {
      onOpenChange(false);
    }
    navigateTo(path)(e);
  };

  const renderRowLinkCell = (path: string, label: string) => (
    <td className='py-1.5 pl-2 text-right'>
      <Button
        variant='ghost'
        size='icon'
        type='button'
        title={label}
        aria-label={label}
        onClick={navigateAndClose(path)}
      >
        <ExternalLink className='h-3.5 w-3.5' />
      </Button>
    </td>
  );

  useEffect(() => {
    if (!billingCycle) {
      setPreview(null);
      setHippsCode(null);
      setHippsUnavailableReason(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [previewResponse, hippsResponse] = await Promise.all([
          getBillingCycleEoePreview({
            path: { billing_cycle_id: billingCycle.billing_cycle_id },
          }),
          getBillingCycleHippsPreview({
            path: { billing_cycle_id: billingCycle.billing_cycle_id },
          }),
        ]);
        if (cancelled) return;
        // The generated client resolves HTTP failures as { error } rather than
        // rejecting. Without this check a failed request would read as "no
        // HIPPS code" — a claim-readiness verdict — instead of a load failure.
        if (previewResponse?.error || hippsResponse?.error) {
          console.error(
            'Failed to load EOE preview:',
            previewResponse?.error ?? hippsResponse?.error
          );
          setPreview(null);
          setHippsCode(null);
          setHippsUnavailableReason(null);
          setError('Unable to load billing cycle details.');
          return;
        }
        setPreview(previewResponse?.data ?? null);
        setHippsCode(hippsResponse?.data?.hipps_code ?? null);
        setHippsUnavailableReason(
          hippsResponse?.data?.unavailable_reason ?? null
        );
      } catch (err) {
        console.error('Failed to load EOE preview:', err);
        if (cancelled) return;
        setPreview(null);
        setHippsCode(null);
        setHippsUnavailableReason(null);
        setError('Unable to load billing cycle details.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billingCycle]);

  useEffect(() => {
    if (!billingCycle?.is_authorization_required || !billingCycle.episode_id) {
      setAuthData(null);
      setAuthLoadFailed(false);
      return;
    }
    let cancelled = false;
    setAuthLoadFailed(false);
    // Clear prior cycle's data so switching cycles shows loading, not stale auths.
    setAuthData(null);
    (async () => {
      try {
        const [a, e, v] = await Promise.all([
          listEpisodeAuthorizations({
            path: { episode_id: billingCycle.episode_id },
          }),
          getAuthorizationErrors({
            path: { episode_id: billingCycle.episode_id },
          }),
          getPatientVisits({
            path: { patient_id: billingCycle.patient_id },
          }),
        ]);
        if (cancelled) return;
        // The generated client resolves HTTP failures as { error } rather than
        // rejecting, so a failed fetch must be treated as a load failure (not
        // empty-but-successful data).
        if (a.error || e.error || v.error) {
          console.error(
            'Failed to load authorizations for claim preview',
            a.error ?? e.error ?? v.error
          );
          setAuthLoadFailed(true);
          return;
        }
        const episodeVisits = (v.data ?? []).filter(
          (visit: VisitResponse) => visit.episode_id === billingCycle.episode_id
        );
        setAuthData({
          auths: a.data ?? [],
          errors: e.data?.errors ?? [],
          visits: episodeVisits,
        });
      } catch (err) {
        console.error('Failed to load authorizations for claim preview', err);
        if (!cancelled) setAuthLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billingCycle?.episode_id, billingCycle?.is_authorization_required]);

  const getMiddleInitial = (middleName?: string | null): string =>
    middleName?.trim()?.charAt(0)?.toUpperCase() || '-';

  const displayValue = (value?: string | null): string =>
    value && value.trim() !== '' ? value : '-';

  const displayDate = (value?: string | null): string =>
    formatDateToMMDDYYYY(value) || '-';

  const statusLabel = (s: string) =>
    STATUS_DISPLAY_LABELS[s] ??
    s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const renderStatusBadge = (s: string) => (
    <Badge variant={getStatusBadgeVariant(s as never)}>{statusLabel(s)}</Badge>
  );

  const demographics = preview?.patient_demographics;
  const address = preview?.patient_address;
  const physician = preview?.physician_information;
  const episodeInfo = preview?.episode_information;
  const admissionInfo = preview?.admission_information;

  const patientFields: FieldDef[] = [
    { label: 'Last name', value: displayValue(demographics?.last_name) },
    { label: 'First name', value: displayValue(demographics?.first_name) },
    {
      label: 'Middle initial',
      value: getMiddleInitial(demographics?.middle_name),
    },
    { label: 'Gender', value: displayValue(demographics?.gender) },
    {
      label: 'Date of birth',
      value: displayDate(demographics?.date_of_birth),
    },
    ...buildInsuranceIdFields(demographics, 'eoe-mbi'),
    {
      label: 'Medical record number',
      value: displayValue(demographics?.mrn),
      mono: true,
    },
    { label: 'Phone number', value: displayValue(demographics?.phone) },
    { label: 'Address', value: displayValue(address?.address), span2: true },
    { label: 'City', value: displayValue(address?.city) },
    { label: 'State', value: displayValue(address?.state) },
    { label: 'Zip', value: displayValue(address?.zip_code) },
  ];

  const physicianFields: FieldDef[] = [
    { label: 'Last name', value: displayValue(physician?.last_name) },
    { label: 'First name', value: displayValue(physician?.first_name) },
    { label: 'Physician NPI', value: displayValue(physician?.npi), mono: true },
    { label: 'PECOS status', value: displayValue(physician?.pecos_status) },
  ];

  const episodeAdmissionFields: FieldDef[] = [
    { label: 'Type of bill', value: displayValue(episodeInfo?.type_of_bill) },
    {
      label: 'Start of care date',
      value: displayDate(episodeInfo?.soc_date),
    },
    {
      label: 'Assessment date',
      value: displayDate(episodeInfo?.assessment_date),
    },
    {
      label: 'Episode start date',
      value: displayDate(episodeInfo?.episode_start_date),
    },
    {
      label: 'First billable visit',
      value: displayDate(episodeInfo?.first_billable_visit),
    },
    {
      label: 'Point of origin',
      value: displayValue(admissionInfo?.point_of_origin),
    },
    {
      label: 'Admission type',
      value: displayValue(admissionInfo?.admission_type),
    },
    {
      label: 'Condition codes',
      value:
        admissionInfo?.condition_codes &&
        admissionInfo.condition_codes.length > 0
          ? admissionInfo.condition_codes.join(', ')
          : '-',
      mono: true,
      span2: true,
    },
    {
      label: 'Admission diagnosis code',
      value: displayValue(admissionInfo?.admission_diagnosis_code),
      mono: true,
    },
  ];

  const valueCodes = admissionInfo?.value_codes ?? [];

  const checks: CheckItem[] = billingCycle
    ? (() => {
        const items: CheckItem[] = [];

        items.push({
          key: 'oasis',
          label: 'OASIS',
          status: billingCycle.all_oasis_correct_state ? 'pass' : 'fail',
          message: billingCycle.all_oasis_correct_state
            ? 'All OASIS visits are signed and exported'
            : 'OASIS visits must be signed and exported',
          anchor: 'eoe-oasis-visits',
        });

        const trimmedHipps = hippsCode?.trim() ?? '';
        items.push({
          key: 'hipps',
          label: 'HIPPS',
          status: trimmedHipps !== '' ? 'pass' : 'fail',
          message:
            trimmedHipps !== ''
              ? trimmedHipps
              : (hippsUnavailableReason ?? 'HIPPS code could not be generated'),
          anchor: 'eoe-diagnoses',
        });

        items.push({
          key: 'visits',
          label: 'Visits',
          status: billingCycle.all_visits_completed ? 'pass' : 'fail',
          message: billingCycle.all_visits_completed
            ? 'All billable visits are completed'
            : 'Not all billable visits are completed',
          anchor: 'eoe-visits',
        });

        items.push({
          key: 'orders',
          label: 'Orders',
          status: billingCycle.all_orders_signed ? 'pass' : 'fail',
          message: billingCycle.all_orders_signed
            ? 'All billing orders are satisfied'
            : 'Not all billing orders are satisfied',
          anchor: 'eoe-orders',
        });

        if (SHOW_F2F_CHECK) {
          items.push({
            key: 'f2f',
            label: 'F2F',
            status: billingCycle.is_f2f_valid ? 'pass' : 'fail',
            message: billingCycle.is_f2f_valid
              ? 'F2F document is valid'
              : 'Document not attached or signed',
          });
        }

        // For Medicare plans the demographics panel shows "Member ID" (from
        // patient_insurance) in place of MBI, so label the check to match the
        // field it scrolls to and avoid a mismatch.
        const idLabel = usesPatientInsuranceId(demographics)
          ? 'Member ID'
          : 'MBI';
        if (!billingCycle.is_mbi_required) {
          items.push({
            key: 'medicare',
            label: idLabel,
            status: 'pass',
            message: `${idLabel} not required for this claim`,
            anchor: 'eoe-mbi',
          });
        } else {
          items.push({
            key: 'medicare',
            label: idLabel,
            status: billingCycle.is_mbi_valid ? 'pass' : 'fail',
            message: billingCycle.is_mbi_valid
              ? `${idLabel} is valid`
              : `${idLabel} is missing`,
            anchor: 'eoe-mbi',
          });
        }

        if (billingCycle.is_pcr_tracking_number_required) {
          items.push({
            key: 'pcr',
            label: 'PCR tracking #',
            status: billingCycle.is_pcr_tracking_number_valid ? 'pass' : 'fail',
            message: billingCycle.is_pcr_tracking_number_valid
              ? 'PCR tracking number is set for this billing period'
              : 'PCR tracking number is missing for this billing period',
          });
        }

        if (billingCycle.is_authorization_required) {
          items.push({
            key: 'authorization',
            label: 'Authorization',
            status: billingCycle.is_authorization_valid ? 'pass' : 'fail',
            message: billingCycle.is_authorization_valid
              ? 'Authorization on file'
              : 'Authorization issues — see Authorizations',
            anchor: 'eoe-authorizations',
          });
        }

        return items;
      })()
    : [];

  const blockingCount = checks.filter(c => c.status === 'fail').length;

  const chartPath =
    billingCycle?.patient_id && billingCycle?.episode_id
      ? patientEpisodePath(billingCycle.patient_id, billingCycle.episode_id)
      : null;

  const patientName = billingCycle?.patient_name || '-';

  const insuranceType = billingCycle?.insurance_type
    ? billingCycle.insurance_type
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
    : null;

  const headerRest = billingCycle
    ? [
        `MRN ${billingCycle.patient_mrn || '-'}`,
        `${displayDate(billingCycle.start_date)} – ${displayDate(
          billingCycle.end_date
        )}`,
        billingCycle.total_charges || '-',
        `HIPPS ${hippsCode?.trim() ? hippsCode : '-'}`,
      ].join(' · ')
    : '';

  const renderField = (field: FieldDef) => (
    <div
      key={field.label}
      data-field-anchor={field.anchor}
      className={field.span2 ? 'col-span-2' : undefined}
    >
      <div className='text-xs text-muted-foreground'>{field.label}</div>
      <div
        className={`text-sm font-medium text-foreground ${
          field.mono ? 'font-mono' : ''
        }`}
      >
        {field.value}
      </div>
    </div>
  );

  const renderFieldSection = (title: string, fields: FieldDef[]) => (
    <section key={title}>
      <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        {title}
      </h3>
      <div className='grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4'>
        {fields.map(renderField)}
      </div>
    </section>
  );

  const renderValueCodesSection = () => (
    <section>
      <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        Value codes
      </h3>
      {valueCodes.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No value codes on the claim worksheet.
        </p>
      ) : (
        <table className='w-full max-w-md text-sm'>
          <thead>
            <tr className='border-b text-left text-xs text-muted-foreground'>
              <th className='py-1.5 pr-4 font-medium'>Code</th>
              <th className='py-1.5 font-medium'>Amount</th>
            </tr>
          </thead>
          <tbody>
            {valueCodes.map((vc, idx) => (
              <tr
                key={`${vc.code}-${idx}`}
                className='border-b border-border/50'
              >
                <td className='py-1.5 pr-4 font-mono whitespace-nowrap'>
                  {vc.code}
                </td>
                <td className='py-1.5'>{displayValue(vc.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );

  const renderDiagnosesSection = () => {
    const section = preview?.oasis_diagnoses_from_visit;
    return (
      <section data-field-anchor='eoe-diagnoses'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Diagnoses
        </h3>
        {!section ? (
          <p className='text-sm text-muted-foreground'>
            No diagnosis codes on OASIS visits linked to this billing cycle.
          </p>
        ) : (
          <>
            <p className='mb-2 text-xs text-muted-foreground'>
              Latest visit with diagnoses: {section.source_visit_task}
              {section.source_visit_date
                ? ` · ${displayDate(section.source_visit_date)}`
                : null}
            </p>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b text-left text-xs text-muted-foreground'>
                  <th className='py-1.5 pr-4 font-medium'>Role</th>
                  <th className='py-1.5 pr-4 font-medium'>Code</th>
                  <th className='py-1.5 font-medium'>Description</th>
                </tr>
              </thead>
              <tbody>
                {section.diagnoses.map(dx => (
                  <tr
                    key={`${dx.code}-${dx.position}`}
                    className='border-b border-border/50'
                  >
                    <td className='py-1.5 pr-4 whitespace-nowrap'>
                      {dx.is_primary ? (
                        <span className='rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary'>
                          Primary
                        </span>
                      ) : (
                        'Secondary'
                      )}
                    </td>
                    <td className='py-1.5 pr-4 font-mono whitespace-nowrap'>
                      {dx.code}
                    </td>
                    <td className='py-1.5'>{displayValue(dx.description)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    );
  };

  const renderOasisVisitsSection = () => {
    const visits = preview?.oasis_visits ?? [];
    return (
      <section data-field-anchor='eoe-oasis-visits'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          OASIS visits
        </h3>
        {visits.length === 0 ? (
          <p className='text-sm text-muted-foreground'>No OASIS visits found</p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='py-1.5 pr-4 font-medium'>Task</th>
                <th className='py-1.5 pr-4 font-medium'>Status</th>
                <th className='py-1.5 pr-4 font-medium'>Assigned</th>
                <th className='py-1.5 pr-4 font-medium'>Target date</th>
                <th className='py-1.5 font-medium'>Visit date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visits.map(visit => (
                <tr
                  key={`${visit.task}-${visit.visit_date}-${visit.target_date}`}
                  className={`border-b border-border/50 ${visit.is_billing_ready === false ? 'bg-red-50' : ''}`}
                >
                  <td className='py-1.5 pr-4'>{visit.task}</td>
                  <td className='py-1.5 pr-4'>
                    {renderStatusBadge(visit.status)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.assigned_user)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayDate(visit.target_date)}
                  </td>
                  <td className='py-1.5'>{displayDate(visit.visit_date)}</td>
                  {renderRowLinkCell(
                    patientVisitPath(
                      billingCycle!.patient_id,
                      visit.visit_id,
                      billingCycle!.episode_id ?? undefined
                    ),
                    'View chart'
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderVisitsSection = () => {
    const visitsData = preview?.visits ?? [];
    return (
      <section data-field-anchor='eoe-visits'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Visits
        </h3>
        {visitsData.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No non-OASIS visits found
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='py-1.5 pr-4 font-medium'>Task</th>
                <th className='py-1.5 pr-4 font-medium'>Status</th>
                <th className='py-1.5 pr-4 font-medium'>Assigned</th>
                <th className='py-1.5 pr-4 font-medium'>Target date</th>
                <th className='py-1.5 font-medium'>Visit date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visitsData.map(visit => (
                <tr
                  key={`${visit.task}-${visit.visit_date}-${visit.target_date}`}
                  className={`border-b border-border/50 ${visit.is_billing_ready === false ? 'bg-red-50' : ''}`}
                >
                  <td className='py-1.5 pr-4'>{visit.task}</td>
                  <td className='py-1.5 pr-4'>
                    {renderStatusBadge(visit.status)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(visit.assigned_user)}
                  </td>
                  <td className='py-1.5 pr-4'>
                    {displayDate(visit.target_date)}
                  </td>
                  <td className='py-1.5'>{displayDate(visit.visit_date)}</td>
                  {renderRowLinkCell(
                    patientVisitPath(
                      billingCycle!.patient_id,
                      visit.visit_id,
                      billingCycle!.episode_id ?? undefined
                    ),
                    'View chart'
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderOrdersSection = () => {
    const ordersData = preview?.orders ?? [];
    return (
      <section data-field-anchor='eoe-orders'>
        <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
          Orders
        </h3>
        {ordersData.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No billing orders found
          </p>
        ) : (
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-xs text-muted-foreground'>
                <th className='py-1.5 pr-4 font-medium'>Order</th>
                <th className='py-1.5 pr-4 font-medium'>Date</th>
                <th className='py-1.5 pr-4 font-medium'>Status</th>
                <th className='py-1.5 pr-4 font-medium'>Physician</th>
                <th className='py-1.5 font-medium'>For billing</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ordersData.map((o, idx) => (
                <tr
                  key={`${o.order_type_display}-${o.order_date}-${idx}`}
                  className={`border-b border-border/50 ${o.is_billing_ready === false ? 'bg-red-50' : ''}`}
                >
                  <td className='py-1.5 pr-4'>{o.order_type_display}</td>
                  <td className='py-1.5 pr-4'>{displayDate(o.order_date)}</td>
                  <td className='py-1.5 pr-4'>{renderStatusBadge(o.status)}</td>
                  <td className='py-1.5 pr-4'>
                    {displayValue(o.physician_name)}
                  </td>
                  <td className='py-1.5'>
                    {o.required_for_billing ? 'Required' : 'Optional'}
                  </td>
                  {canViewOrders ? (
                    renderRowLinkCell(
                      billingOrderPath({
                        orderId: o.order_id,
                        status: o.status,
                        patientId: billingCycle!.patient_id,
                        episodeId: billingCycle!.episode_id,
                      }),
                      'View order'
                    )
                  ) : (
                    <td className='py-1.5 pl-2 text-right' />
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderAuthorizationsSection = () => {
    if (!billingCycle?.is_authorization_required) return null;

    const isValid = billingCycle.is_authorization_valid;

    return (
      <section data-field-anchor='eoe-authorizations'>
        <div className='mb-2 flex items-center gap-2'>
          <h3 className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
            Authorizations
          </h3>
          {isValid ? (
            <CheckCircle2 className='h-3.5 w-3.5 text-green-600' />
          ) : (
            <XCircle className='h-3.5 w-3.5 text-red-600' />
          )}
        </div>
        {authLoadFailed ? (
          <p className='text-sm text-muted-foreground'>
            Unable to load authorizations
          </p>
        ) : authData === null ? (
          <p className='text-sm text-muted-foreground'>
            Loading authorizations…
          </p>
        ) : authData.auths.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No authorizations on file.
          </p>
        ) : (
          <div className='space-y-2'>
            {authData.auths.map(auth => (
              <div
                key={auth.id}
                className='rounded-md border border-border/60 px-3 py-2 text-sm'
              >
                <div className='flex items-center gap-3'>
                  <span className='font-medium'>
                    {auth.authorization_number
                      ? `#${auth.authorization_number}`
                      : '—'}
                  </span>
                  {auth.status && (
                    <Badge
                      variant={getStatusBadgeVariant(auth.status as never)}
                    >
                      {auth.status}
                    </Badge>
                  )}
                  <span className='text-muted-foreground'>
                    {formatDateToMMDDYYYY(auth.start_date)} –{' '}
                    {formatDateToMMDDYYYY(auth.end_date)}
                  </span>
                </div>
                <div className='mt-1 flex flex-wrap gap-3'>
                  {AUTH_DISCIPLINE_FIELDS.map(f => {
                    const allowed = auth[f.key] as number | null | undefined;
                    if (allowed == null) return null;
                    // Readiness ignores DENIED/CANCELLED auths, so don't show
                    // used/over-budget styling for inactive ones — just the budget.
                    const isActive =
                      auth.status !== 'DENIED' && auth.status !== 'CANCELLED';
                    if (!isActive) {
                      return (
                        <span
                          key={f.key}
                          className='text-xs text-muted-foreground'
                        >
                          {f.label}: {allowed}
                        </span>
                      );
                    }
                    const usedCount = (authData?.visits ?? []).filter(
                      v =>
                        v.authorization_id === auth.id &&
                        v.discipline === f.label
                    ).length;
                    const isOver = usedCount > allowed;
                    return (
                      <span
                        key={f.key}
                        className={`text-xs ${isOver ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}
                      >
                        {f.label}: {usedCount}/{allowed}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {authData !== null &&
          authData.auths.length > 0 &&
          authData.errors.length > 0 && (
            <ul className='mt-2 space-y-1'>
              {authData.errors.map((err, idx) => (
                <li key={idx} className='text-xs text-red-700'>
                  {err.message}
                </li>
              ))}
            </ul>
          )}
        <button
          type='button'
          onClick={navigateAndClose(
            patientAuthorizationsPath(
              billingCycle.patient_id,
              billingCycle.episode_id
            )
          )}
          className='mt-2 text-xs font-medium text-primary underline-offset-2 hover:underline'
        >
          View authorizations ↗
        </button>
      </section>
    );
  };

  const renderRail = () => (
    <aside className='min-h-0 overflow-y-auto border-r bg-muted/40 px-4 py-4'>
      <div
        className={`mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${
          blockingCount > 0
            ? 'border-red-200 bg-red-50 text-red-800'
            : 'border-green-200 bg-green-50 text-green-800'
        }`}
      >
        {blockingCount > 0 ? (
          <>
            <XCircle className='h-4 w-4 shrink-0 text-red-600' />
            <span>
              Not ready
              <span className='font-normal'> · {blockingCount} blocking</span>
            </span>
          </>
        ) : (
          <>
            <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
            <span>Ready to bill</span>
          </>
        )}
      </div>
      <h3 className='mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        Readiness checks
      </h3>
      <ul className='space-y-2'>
        {checks.map(check => {
          const isClickable =
            check.status === 'fail' && !!check.anchor && !!preview;

          const body = (
            <>
              <div className='flex items-baseline gap-2'>
                {check.status === 'pass' ? (
                  <CheckCircle2
                    aria-label='Passing'
                    className='h-3.5 w-3.5 shrink-0 self-center text-green-600'
                  />
                ) : (
                  <XCircle
                    aria-label='Failing'
                    className='h-3.5 w-3.5 shrink-0 self-center text-red-600'
                  />
                )}
                <span className='text-sm font-medium text-foreground'>
                  {check.label}
                </span>
              </div>
              <p
                className={`ml-[1.375rem] text-xs leading-snug ${
                  check.status === 'fail'
                    ? 'text-red-700'
                    : 'text-muted-foreground'
                } ${
                  check.key === 'hipps' && check.status === 'pass'
                    ? 'font-mono'
                    : ''
                }`}
              >
                {check.message}
                {isClickable ? (
                  <span className='ml-1 whitespace-nowrap font-medium underline underline-offset-2'>
                    View ↓
                  </span>
                ) : null}
              </p>
            </>
          );

          return (
            <li key={check.key}>
              {isClickable ? (
                <button
                  type='button'
                  onClick={() =>
                    check.anchor &&
                    scrollToField(check.anchor, { focus: false })
                  }
                  className='w-full rounded-md text-left transition-colors hover:bg-red-50'
                  aria-label={`${check.label}: ${check.message}. View details.`}
                >
                  {body}
                </button>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );

  return (
    <Dialog open={billingCycle !== null} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0'>
        <DialogHeader className='border-b px-6 py-4'>
          <DialogTitle>EOE Claim Preview</DialogTitle>
          <DialogDescription className='sr-only'>
            Preview the end-of-episode claim readiness checks and claim details.
          </DialogDescription>
          {billingCycle ? (
            <>
              <p className='text-sm font-medium text-foreground'>
                {billingCycle.insurance_name || '-'}
                {insuranceType ? (
                  <span className='ml-2 font-normal text-muted-foreground'>
                    · {insuranceType}
                  </span>
                ) : null}
              </p>
              <p className='text-sm text-muted-foreground'>
                {chartPath ? (
                  <Link
                    to={chartPath}
                    className='font-medium text-primary underline-offset-4 hover:underline'
                  >
                    {patientName}
                  </Link>
                ) : (
                  patientName
                )}
                {` · ${headerRest}`}
                {chartPath ? (
                  <>
                    {' · '}
                    <Link
                      to={chartPath}
                      className='font-medium text-primary underline-offset-4 hover:underline'
                    >
                      View episode <span aria-hidden='true'>↗</span>
                    </Link>
                  </>
                ) : null}
              </p>
            </>
          ) : null}
        </DialogHeader>

        {isLoading ? (
          <div className='flex flex-1 flex-col items-center justify-center py-10 text-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            <p className='mt-2 text-sm'>Loading billing cycle details...</p>
          </div>
        ) : (
          <div className='grid flex-1 min-h-0 grid-cols-1 lg:grid-cols-[14rem_1fr]'>
            {billingCycle ? renderRail() : null}
            <main className='min-h-0 overflow-y-auto px-6 py-4'>
              {error ? (
                <Alert className='border-red-200 bg-red-50'>
                  <AlertCircle className='h-4 w-4 text-red-600' />
                  <AlertDescription className='text-red-800'>
                    <p>
                      An error happened while loading billing cycle details.
                      Please try again and contact support if the issue happens
                      again.
                    </p>
                    <p className='mt-2'>{error}</p>
                  </AlertDescription>
                </Alert>
              ) : billingCycle && preview ? (
                <div className='space-y-5'>
                  {renderFieldSection('Patient', patientFields)}
                  {renderFieldSection('Physician', physicianFields)}
                  {renderFieldSection(
                    'Episode & Admission',
                    episodeAdmissionFields
                  )}
                  {renderValueCodesSection()}
                  {renderDiagnosesSection()}
                  {renderOasisVisitsSection()}
                  {renderVisitsSection()}
                  {renderOrdersSection()}
                  {renderAuthorizationsSection()}
                </div>
              ) : (
                <div className='flex min-h-[12rem] flex-col items-center justify-center text-center'>
                  <AlertCircle className='h-5 w-5 text-muted-foreground' />
                  <p className='mt-2 text-sm text-muted-foreground'>
                    No claim details available for this billing cycle.
                  </p>
                </div>
              )}
            </main>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EOEPreviewPopup;
